import { useEffect, useState } from 'react'
import { useAccessToken } from './useAccessToken'
import * as jose from 'jose'
import { JWTClaimValidationFailed } from 'jose/dist/types/util/errors'
import { XAPIPermissions } from 'types/custom.d.ts'

export type TAccessPermissions = {
  permissions: Set<XAPIPermissions>
  error: JWTClaimValidationFailed | null
  loaded: string | null
}

const default_state = {
  permissions: new Set<XAPIPermissions>(),
  error: null,
  loaded: null,
}

function useAccessPermissions() {
  const accessToken = useAccessToken()

  const [state, setState] = useState<TAccessPermissions>(default_state)

  useEffect(() => {
    async function handleValidateJWTAsync() {
      if (!accessToken) {
        setState(prev => ({ ...prev, loaded: null }))
        return
      }

      if (state.loaded === accessToken) {
        return
      }

      try {
        const signingKeysURL = new URL(
          process.env.REACT_APP_AUTH0_SIGNING_KEYS_URL
        )

        const JWKS = jose.createRemoteJWKSet(signingKeysURL)
        const expectedIssuer = process.env.REACT_APP_AUTH0_ISSUER
        const expectedAudience = process.env.REACT_APP_AUTH0_AUDIENCE

        const { payload } = await jose.jwtVerify(accessToken, JWKS, {
          issuer: expectedIssuer,
          audience: expectedAudience,
        })

        const permissionsList: XAPIPermissions[] =
          (payload.permissions as XAPIPermissions[]) ?? []

        const permissionsSet: Set<XAPIPermissions> = new Set()

        for (const permissionString of permissionsList) {
          permissionsSet.add(permissionString)
        }

        setState(prev => ({
          ...prev,
          permissions: permissionsSet,
          error: null,
          loaded: accessToken,
        }))
      } catch (err) {
        setState(prev => ({
          ...prev,
          permissions: new Set(),
          error: err as JWTClaimValidationFailed,
          loaded: accessToken,
        }))
      }
    }

    handleValidateJWTAsync()
  }, [accessToken])
  return state
}

export default useAccessPermissions
